import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { infaqService, DonationCampaign } from '../../services/infaqService';
import { useOpenMaintenanceCampaigns } from '../../hooks/useMaintenanceInfaq';
import type { PublicCampaign } from '../../services/maintenance-infaq.service';
import LoadingSpinner from '../../components/LoadingSpinner';

function InfaqPage() {
  const [campaigns, setCampaigns] = useState<DonationCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statistics, setStatistics] = useState<{ totalRaised: number; totalDonors: number; activeCampaigns: number } | null>(null);
  const { data: maintenanceCampaigns } = useOpenMaintenanceCampaigns();

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [campaignsData, statsData] = await Promise.all([
        infaqService.getActiveCampaigns(),
        infaqService.getStatistics(),
      ]);
      setCampaigns(campaignsData);
      setStatistics(statsData);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat kempen');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-[#0D7330] text-white rounded-lg hover:bg-[#0a5c26] transition-colors"
          >
            Cuba Lagi
          </button>
        </div>
      </div>
    );
  }

  const totalStats = {
    totalRaised: statistics?.totalRaised || 0,
    totalUtilized: 0, // We'll calculate this from campaigns
    remaining: (statistics?.totalRaised || 0) - 0,
    totalDonors: statistics?.totalDonors || 0,
    utilizationPercentage: 0,
  };

  return (
    <section className="py-12 px-6 md:p-24 bg-neutral-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-[38px] md:text-[46px] font-[CormorantGaramond] font-bold text-gray-900 mb-4">
            Infaq & Derma
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Sumbang untuk amal jariah yang berkekalan. Setiap dermaan anda digunakan dengan telus dan
            bertanggungjawab untuk kebaikan ummah.
          </p>
        </div>

        {/* Total Statistics */}
        {statistics && (
          <div className="bg-white rounded-2xl shadow-lg p-8 mb-12">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-2">Jumlah Dikutip</p>
                <p className="text-2xl md:text-3xl font-bold text-[#0D7330]">
                  {infaqService.formatCurrency(totalStats.totalRaised)}
                </p>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-2">Digunakan</p>
                <p className="text-2xl md:text-3xl font-bold text-[#16592E]">
                  {infaqService.formatCurrency(totalStats.totalUtilized)}
                </p>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-2">Baki</p>
                <p className="text-2xl md:text-3xl font-bold text-[#0D7330]">
                  {infaqService.formatCurrency(totalStats.remaining)}
                </p>
              </div>
              <div className="p-4">
                <p className="text-sm text-gray-600 mb-2">Jumlah Derma</p>
                <p className="text-2xl md:text-3xl font-bold text-gray-900">
                  {totalStats.totalDonors}
                </p>
              </div>
            </div>
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>Telus Penggunaan Dana</span>
                <span className="font-semibold text-[#0D7330]">{totalStats.utilizationPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-[#0D7330] h-3 rounded-full transition-all duration-500"
                  style={{ width: `${totalStats.utilizationPercentage}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Campaign Cards */}
        {campaigns.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="w-16 h-16 mx-auto text-gray-400 mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Tiada Kempen Aktif</h3>
            <p className="text-gray-600">Sila kembali semula kemudian untuk kempen-kempen akan datang.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {campaigns.map((campaign) => (
              <div
                key={campaign.id}
                className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow"
              >
                {/* Campaign Image */}
                <div className="h-48 overflow-hidden">
                  <img
                    src={campaign.featuredImage || '/demo-help.jpg'}
                    alt={campaign.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Campaign Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{campaign.title}</h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{campaign.description}</p>

                  {/* Progress Bar */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-600">Dikumpul</span>
                      <span className="font-semibold text-[#0D7330]">
                        {infaqService.formatCurrency(campaign.currentAmount)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#0D7330] h-2 rounded-full transition-all duration-500"
                        style={{
                          width: `${infaqService.calculateProgress(campaign.currentAmount, campaign.targetAmount)}%`,
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>Sasaran: {infaqService.formatCurrency(campaign.targetAmount)}</span>
                      <span>
                        {Math.round(infaqService.calculateProgress(campaign.currentAmount, campaign.targetAmount))}%
                      </span>
                    </div>
                  </div>

                  {/* Campaign Stats */}
                  <div className="flex justify-between text-xs text-gray-600 mb-4">
                    <span>{campaign.donorCount} penderma</span>
                    {campaign.daysLeft !== null && campaign.daysLeft !== undefined && (
                      <span>{campaign.daysLeft} hari lagi</span>
                    )}
                  </div>

                  {/* Donate Button */}
                  <Link
                    to={`/infaq/campaign/${campaign.slug}`}
                    className="block w-full bg-[#0D7330] text-white text-center py-3 rounded-lg font-semibold hover:bg-[#0a5c26] transition-colors"
                  >
                    Infaq Sekarang
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Maintenance Campaigns */}
        {maintenanceCampaigns && maintenanceCampaigns.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <svg
                className="w-6 h-6 text-[#0D7330]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z"
                />
              </svg>
              <h3 className="text-2xl font-[CormorantGaramond] font-bold text-gray-900">
                Kempen Penyelenggaraan
              </h3>
            </div>
            <p className="text-sm text-gray-600 mb-6">
              Bantu penyedia perkhidmatan menyelenggara kenderaan dan aset mereka untuk terus
              memberi khidmat bantuan kepada ummah.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {maintenanceCampaigns.map((c: PublicCampaign) => {
                const pct =
                  c.targetAmount > 0
                    ? Math.min(100, Math.round((c.raisedAmount / c.targetAmount) * 100))
                    : 0;
                return (
                  <div
                    key={c.id}
                    className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow"
                  >
                    <div className="h-2 bg-amber-500" />
                    <div className="p-6">
                      <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800 mb-2">
                        Penyelenggaraan
                      </span>
                      <h4 className="text-lg font-bold text-gray-900 mb-1">{c.title}</h4>
                      <p className="text-xs text-gray-600 mb-1">oleh {c.providerName}</p>
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{c.reason}</p>

                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-gray-600">Dikumpul</span>
                          <span className="font-semibold text-[#0D7330]">
                            {infaqService.formatCurrency(c.raisedAmount)}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-[#0D7330] h-2 rounded-full transition-all duration-500"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <div className="flex justify-between text-xs text-gray-500 mt-1">
                          <span>Sasaran: {infaqService.formatCurrency(c.targetAmount)}</span>
                          <span>{pct}%</span>
                        </div>
                      </div>

                      <div className="flex justify-between text-xs text-gray-600 mb-4">
                        <span>{c.donorCount} penderma</span>
                        {c.daysLeft !== null && c.daysLeft !== undefined && (
                          <span>{c.daysLeft} hari lagi</span>
                        )}
                      </div>

                      <Link
                        to={`/infaq/maintenance/${c.slug}`}
                        className="block w-full bg-[#0D7330] text-white text-center py-3 rounded-lg font-semibold hover:bg-[#0a5c26] transition-colors"
                      >
                        Infaq Sekarang
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Transparency Note */}
        <div className="bg-[#FAF9F6] rounded-xl p-6 border border-gray-200">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <svg
                className="w-8 h-8 text-[#0D7330]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="text-lg font-semibold text-gray-900 mb-2">Telus & Amanah</h4>
              <p className="text-sm text-gray-600 mb-3">
                Setiap sen dermaan anda direkodkan dan digunakan dengan telus. Anda boleh melihat bagaimana
                dana diagihkan dengan bukti resit dan dokumen sokongan.
              </p>
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Pembayaran melalui AiAhad</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Laporan penggunaan dana</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Resit untuk cukai</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Home Link */}
        <div className="text-center mt-12">
          <Link
            to="/#infaq"
            className="inline-flex items-center gap-2 text-[#0D7330] hover:underline font-semibold"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Kembali ke Laman Utama
          </Link>
        </div>
      </div>
    </section>
  );
}

export default InfaqPage;
